'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Runs the callback when the App Router pathname (or hash, if opted in)
 * changes — but NOT on initial mount and NOT when `callback` identity
 * changes.
 *
 * Why the ref dance?
 * ------------------
 * The previous implementation depended on `[callback, pathname]` and called
 * `callback()` inside the effect. That has two non-obvious bugs:
 *
 *   1. It runs on initial mount (pathname hasn't changed; we're just seeing
 *      it for the first time).
 *   2. It re-fires whenever `callback` identity changes — even when the
 *      route is the same. Many callers pass a `useCallback` whose deps
 *      include state that changes during normal interaction (e.g. the
 *      Header's `closeDesktopMenus` depends on `showSearch`). Toggling
 *      `showSearch` true caused the callback to be recreated, which
 *      triggered this effect, which called the callback, which set
 *      `showSearch` back to false. Net result: the search panel snapped
 *      shut the instant it opened.
 *
 * Storing the latest callback in a ref decouples its identity from the
 * effect's run schedule. The effect now fires exclusively when `pathname`
 * actually changes.
 */
export const useOnRouteChange = (
  callback: () => void,
  _legacyEvents?: string[],
  runOnHashChange = false
) => {
  const pathname = usePathname();

  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Skip the initial mount; only fire on subsequent pathname changes.
  const previousPathname = useRef<string | null | undefined>(pathname);
  useEffect(() => {
    if (previousPathname.current === pathname) {
      return;
    }
    previousPathname.current = pathname;
    callbackRef.current();
  }, [pathname]);

  useEffect(() => {
    if (!runOnHashChange) return;
    const handler = () => callbackRef.current();
    window.addEventListener('hashchange', handler);
    return () => {
      window.removeEventListener('hashchange', handler);
    };
  }, [runOnHashChange]);
};

export function useOnHashChange(callback: () => void) {
  return useOnRouteChange(callback, undefined, true);
}
